import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subscription, timer } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../environments/environment';
import { TokenStorageService } from './token-storage.service';
import { AuthService } from './auth.service';

export interface CompanyNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
}

/**
 * Messages are plain text and rendered as text. Rows written before that still hold
 * <strong> tags and HTML entities, so those are reduced to their text. DOMParser
 * builds an inert document: nothing in it runs or loads.
 */
export function toPlainText(message: string): string {
  if (!/[<&]/.test(message)) return message;
  return new DOMParser().parseFromString(message, 'text/html').body.textContent ?? '';
}

export interface NotificationListResponse {
  notifications: CompanyNotification[];
  unread_count: number;
}

@Injectable({ providedIn: 'root' })
export class CompanyNotificationService {
  private apiUrl = environment.apiUrl;

  private readonly _notifications = signal<CompanyNotification[]>([]);
  private readonly _unreadCount = signal(0);

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();

  /** @deprecated Compatibility bridge — read the signals instead. */
  readonly notifications$ = toObservable(this._notifications);
  /** @deprecated See {@link notifications$}. */
  readonly unreadCount$ = toObservable(this._unreadCount);

  private polling: Subscription | null = null;

  /** Poll interval in milliseconds. */
  private readonly POLL_INTERVAL = 60_000;

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private authService: AuthService,
  ) {}

  /** Start polling. Idempotent — a second call does not add a second loop. */
  startPolling(): void {
    if (this.polling) return;
    this.polling = timer(0, this.POLL_INTERVAL)
      .pipe(
        switchMap(() => this.fetchNotifications()),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  /** Stops the loop, e.g. on logout. */
  stopPolling(): void {
    this.polling?.unsubscribe();
    this.polling = null;
  }

  fetchNotifications(): Observable<NotificationListResponse | null> {
    if (!this.authService.isLoggedIn()) return of(null);
    return this.http.get<NotificationListResponse>(`${this.apiUrl}/notifications?limit=20`).pipe(
      tap((res) => {
        if (res) {
          this._notifications.set(
            res.notifications.map((n) => ({ ...n, message: toPlainText(n.message) })),
          );
          this._unreadCount.set(res.unread_count);
        }
      }),
      catchError(() => of(null)),
    );
  }

  markRead(id: number): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/notifications/${id}/read`, {}).pipe(
      tap(() => {
        const updated = this._notifications().map((n) =>
          n.id === id ? { ...n, is_read: true } : n,
        );
        this._notifications.set(updated);
        this._unreadCount.set(updated.filter((n) => !n.is_read).length);
      }),
    );
  }

  /** Close a notification for good: it is deleted, not just marked read. */
  remove(id: number): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/notifications/${id}`).pipe(
      tap(() => {
        const updated = this._notifications().filter((n) => n.id !== id);
        this._notifications.set(updated);
        this._unreadCount.set(updated.filter((n) => !n.is_read).length);
      }),
    );
  }

  markAllRead(): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/notifications/mark-all-read`, {}).pipe(
      tap(() => {
        const updated = this._notifications().map((n) => ({ ...n, is_read: true }));
        this._notifications.set(updated);
        this._unreadCount.set(0);
      }),
    );
  }
}
